
/**
 * HighlightSlider
 * The HighlightSlider which gives a more interactive experience to present car features.
 * The purpose is to present motion content without using videos or motion clips,
 * where the user can drag the slider and see the movement created by a set of images.
 * ValidationBox is available for this component using the slot-name 'validation-box'
 * The component inherits its parent darkmode property.
 */
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import GenericImage from '@owc-packages/GenericImage/src/GenericImage.vue';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import { CLASSES, WB_SLIDER_THEME_VALUES } from './HighlightSlider.config';
import { HighlightSliderComponent } from './Highlight';
import OwcDataComponent from '@owc-utils/Mixins/OwcDataComponent';
import Overlay from '@owc-packages/Overlay/src/Overlay.vue';
import { HIGHLIGHT_TRACKING_EVENT_NAME } from './HighlightUtils';

export default (ComponentInitializeDispatcher as HighlightSliderComponent).extend({
  name: 'owc-highlight',
  components: {
    GenericImage,
    SlotContainer,
    ValidationBoxWrapper,
    Overlay,
  },
  mixins: [OwcDataComponent],
  data() {
    return {
      isDragging: false,
      isOnTransition: false,
      barOffsetWidth: 0,
      sliderBar: this.$refs.sliderBar as HTMLElement,
      sliderButton: this.$refs.sliderButton as HTMLElement,
      slides: [] as HTMLElement[],
      selectedTarget: 0,
      sliderIntervalWidth: 0,
      slidesSlot: null,
    };
  },
  /*
   * Mounted
   *
   * Setup the DOM References, adds the event listeners and changed to the first image
   */
  mounted() {
    this.setupDomReferences();
    // if it is webcomponent mode, we need to listen for the `slotchange` when the component is mounted
    if (this.isBuildWc) {
      this.addEventListeners();
    } else {
      this.setupSlider();
    }
  },
  computed: {
    label() {
      return this.componentData?.label;
    },
    isDarkMode() {
      return Boolean(this.componentData?.darkMode);
    },
    swapTheme() {
      return this.isDarkMode ? WB_SLIDER_THEME_VALUES.darkTheme : WB_SLIDER_THEME_VALUES.lightTheme;
    },
    slideSteps() {
      return this.slides.length - 1;
    },
  },
  methods: {
    changeImages(changeEvent) {
      this.changeImage(changeEvent.detail);

      this.$el?.dispatchEvent(
        new CustomEvent(HIGHLIGHT_TRACKING_EVENT_NAME, {
          detail: changeEvent.detail,
          composed: true,
          bubbles: true,
        }),
      );
    },
    /**
     * setupSlider: Sets to active the first image of the slider and calculates the sliderIntervalWidth
     */
    setupSlider() {
      if (this.slides.length > 0) {
        this.changeImage(0);
      }
    },
    /**
     * changeImage: Changes the class of the active image to the correct one. Furthermore,
     * it removes the active class from the previous image.
     *
     * @param {number} targetIndex The index of the active slide
     */
    changeImage(targetIndex): void {
      this.slides.forEach(slide => {
        slide.classList.remove(CLASSES.highlightSliderSlideActive);
      });
      this.slides[targetIndex].classList.add(CLASSES.highlightSliderSlideActive);
    },
    /**
     * setupDomReferences: Setups and stores all the references needed
     *
     */
    setupDomReferences(): void {
      this.sliderBar = this.$refs.sliderBar as HTMLElement;
      this.sliderButton = this.$refs.sliderButton as HTMLElement;
      // When builded via Webcomponent, we need to get the assigned elements
      // from the slot element inside
      if (this.isBuildWc) {
        this.slidesSlot = this.$el.querySelector(
          // We are receiving 2 slots, the default where the images go
          // and one for ValidationBox, for this case whe want to select
          // the default (the one unnnamed).
          'slot:not([name])',
        ) as HTMLSlotElement;
      } else {
        this.slides = [...this.$el.querySelectorAll(`.${CLASSES.highlightSliderSlide}`)] as HTMLElement[];
      }
    },
    addEventListeners(): void {
      this.slidesSlot?.addEventListener('slotchange', () => {
        this.slides = this.slidesSlot?.assignedElements() as HTMLElement[];
        this.slides = this.slides.filter(el => el.tagName === 'OWC-GENERIC-IMAGE');
        if (!this.isEditMode) {
          this.setupSlider();
        }
      });
    },
  },
});
